/**
 * Central enum definitions for Rules V2 Engine
 */

/**
 * Query Operators
 *
 * Defines the comparison operators that can be used in rule conditions.
 * Operators are grouped by condition type (selection-based vs text-based).
 */
export enum QueryOperator {
  // Selection operators (for Ticket Channel, Topic, Assignment Type)
  IS = "is",
  IS_NOT = "is_not",

  // Text operators (for Subject Line, Message Body, Sent From Email, Received At Email)
  CONTAINS = "contains",
  DOES_NOT_CONTAIN = "does_not_contain",
  IS_EXACTLY = "is_exactly",
  DOES_NOT_EQUAL = "does_not_equal",
}

// Operators for selection-based conditions (Ticket Channel, Topic, Assignment Type)
export const selectionQueryOperators = [
  QueryOperator.IS,
  QueryOperator.IS_NOT,
] as const;
export type SelectionQueryOperator = (typeof selectionQueryOperators)[number];

// Operators for text-based conditions (Subject Line, Message Body, Sent From Email, Received At Email)
export const textQueryOperators = [
  QueryOperator.CONTAINS,
  QueryOperator.DOES_NOT_CONTAIN,
  QueryOperator.IS_EXACTLY,
  QueryOperator.DOES_NOT_EQUAL,
] as const;
export type TextQueryOperator = (typeof textQueryOperators)[number];

/**
 * Date Query Operators
 *
 * Defines comparison operators for date-based conditions (e.g., Last Order Time).
 */
export enum DateQueryOperator {
  WITHIN_LAST = "within_last",
  ON_OR_BEFORE = "on_or_before",
  ON_OR_AFTER = "on_or_after",
  BETWEEN = "between",
  NEVER = "never",
}

export const dateQueryOperators = [
  DateQueryOperator.WITHIN_LAST,
  DateQueryOperator.ON_OR_BEFORE,
  DateQueryOperator.ON_OR_AFTER,
  DateQueryOperator.BETWEEN,
  DateQueryOperator.NEVER,
] as const;
export type DateQueryOperatorType = (typeof dateQueryOperators)[number];

/**
 * Rule Trigger Types
 *
 * Defines when a rule should be evaluated and executed.
 * Each trigger type represents a specific event in the system.
 */
export enum RuleTriggerType {
  CONVERSATION_CREATED = "conversation_created",
  CONVERSATION_MESSAGE_CREATED = "conversation_message_created",
  MERCHANT_MESSAGE_CREATED = "merchant_message_created",
  MESSAGE_RECEIVED = "message_received",
  CONVERSATION_STATUS_CHANGED = "conversation_status_changed",
  TAG_ADDED = "tag_added",
  ASSIGNMENT_CHANGED = "assignment_changed",
  CHAT_CONVERTED_TO_EMAIL = "chat_converted_to_email",
}

/**
 * Rule Condition Types
 *
 * Defines the types of conditions that can be evaluated in a rule.
 * All conditions must pass (AND logic) or at least one must pass (OR logic)
 * depending on the condition group configuration.
 */
export enum RuleConditionType {
  TICKET_CHANNEL = "ticket_channel",
  TICKET_STATUS = "ticket_status",
  TOPIC = "topic",
  SENT_FROM_EMAIL = "sent_from_email",
  RECEIVED_AT_EMAIL = "received_at_email",
  SUBJECT_LINE = "subject_line",
  MESSAGE_BODY_MATCHES = "message_body_matches",
  AFTER_HOURS = "after_hours",
  ASSIGNMENT_TYPE = "assignment_type",
  LAST_ORDER_TIME = "last_order_time",
  MESSAGE_SENTIMENT = "message_sentiment",
}

/**
 * Rule Action Types
 *
 * Defines the types of actions that can be executed when rule conditions pass.
 * Actions are executed sequentially in the order they appear in the rule.
 */
export enum RuleActionType {
  SET_STATUS = "SET_STATUS",
  ADD_TAG = "ADD_TAG",
  AUTO_REPLY = "AUTO_REPLY",
  ASSIGN = "ASSIGN",
}

/**
 * Error Handling Strategies
 *
 * Defines how the rule engine should handle errors during action execution.
 */
export enum ErrorHandlingStrategy {
  /**
   * Stop execution immediately if any action fails
   * Recommended for critical actions where partial execution is unsafe
   */
  FAIL_FAST = "fail_fast",

  /**
   * Continue executing remaining actions even if one fails
   * Failed actions are logged but don't stop the flow
   */
  CONTINUE_ON_ERROR = "continue_on_error",
}
